import React, { FC, useEffect, useMemo, useRef } from 'react';

import { Box } from '@mui/material';

const KOMBO_CONNECT_SDK_VERSION = '1.2.3';
const KOMBO_CONNECT_FRAME_SANDBOX =
  'allow-scripts allow-downloads allow-same-origin allow-top-navigation allow-forms allow-popups allow-pointer-lock allow-popups-to-escape-sandbox';

type KomboConnectMessage =
  | {
      type: 'close';
      data?: unknown;
    }
  | {
      type: 'success';
      data?: {
        activation_token?: string;
      };
    };

export interface Props {
  open: boolean;
  url: string;
  onClose: () => void;
  onSuccess: (activationToken: string) => void;
}

function getFrameUrl(url: string): string {
  const frameUrl = new URL(url);
  frameUrl.searchParams.set('sdkv', KOMBO_CONNECT_SDK_VERSION);
  return frameUrl.toString();
}

const KomboConnectDialog: FC<Props> = ({ open, url, onClose, onSuccess }) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const isSettledRef = useRef(false);

  const frameUrl = useMemo(() => {
    if (!url) {
      return '';
    }
    return getFrameUrl(url);
  }, [url]);

  const frameOrigin = useMemo(() => {
    if (!frameUrl) {
      return '';
    }
    return new URL(frameUrl).origin;
  }, [frameUrl]);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect intentionally performs a side effect after render; revisit before adding logic.
  useEffect(() => {
    if (!open) {
      return;
    }
    isSettledRef.current = false;
  }, [frameUrl, open]);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect manages an external subscription/listener after render and cleanup.
  useEffect(() => {
    if (!open || !frameOrigin) {
      return undefined;
    }

    const handleMessage = (event: MessageEvent<KomboConnectMessage>) => {
      if (isSettledRef.current || event.source !== iframeRef.current?.contentWindow || event.origin !== frameOrigin) {
        return;
      }

      if (event.data?.type === 'close') {
        isSettledRef.current = true;
        onClose();
        return;
      }

      const activationToken = event.data?.type === 'success' ? event.data.data?.activation_token : undefined;
      if (activationToken) {
        isSettledRef.current = true;
        onSuccess(activationToken);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [frameOrigin, onClose, onSuccess, open]);

  if (!open || !frameUrl) {
    return null;
  }

  return (
    <Box
      allow="clipboard-read; clipboard-write"
      component="iframe"
      data-testid="kombo-connect-iframe"
      ref={iframeRef}
      sandbox={KOMBO_CONNECT_FRAME_SANDBOX}
      src={frameUrl}
      title="Kombo Connect"
      sx={{
        border: 0,
        bottom: 0,
        height: '100%',
        left: 0,
        position: 'fixed',
        right: 0,
        top: 0,
        width: '100%',
        zIndex: Number.MAX_SAFE_INTEGER,
      }}
    />
  );
};

export default KomboConnectDialog;
